import React, { useEffect, useMemo, useState } from "react";
import { Button, Group, Table, Text, TextInput, Title, useMantineColorScheme, Container, Badge, Paper, Pagination, Loader, Center } from "@mantine/core";
import { IconPlus, IconSearch, IconArrowLeft } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useListFranchisesQuery } from "generated/graphql";

const PAGE_SIZE = 10;

/**
 * FranchiseManagement (Admin)
 *
 * Lists all franchises with search, pagination, and navigation to edit/create.
 * Uses real API data from the ListFranchises GraphQL query.
 */
export default function FranchiseManagement() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [activePage, setActivePage] = useState(1);
  const [query, setQuery] = useState("");

  const { data, loading, error, refetch } = useListFranchisesQuery({
    variables: {
      skip: (activePage - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    },
    fetchPolicy: "cache-and-network",
  });

  // Map API data to table format
  const franchises = useMemo(() => {
    return (data?.franchises?.items ?? []).map((f) => ({
      id: f.id,
      name: f.name,
      gmName: f.GM?.name ?? "—",
      agmName: f.AGM?.name ?? "—",
      active: f.active,
      prefix: f.prefix ?? "",
    }));
  }, [data]);

  const totalCount = data?.franchises?.totalCount ?? 0;
  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  // Client-side filtering of current page results
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return franchises;
    return franchises.filter((f) =>
      [f.name, f.id, f.gmName, f.prefix].some((v) => v.toLowerCase().includes(q))
    );
  }, [query, franchises]);

  function onSelectFranchise(id: string) {
    // Navigate to the Franchise Edit page for the given franchise id
    navigate(`/admin-hub/franchises/${id}`);
  }

  function onCreateFranchise() {
    // Open the Franchise Create page
    navigate(`/admin-hub/franchises/create`);
  }

  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/admin-hub')}
          size="sm"
        >
          Back to Admin Hub
        </Button>
      </Group>
      <div className="space-y-4">
        <Title c={isDark ? "white" : "black"} size="h3" className="sm:text-2xl">Franchise Management</Title>
        <Text c={isDark ? "gray.4" : "gray.7"} size="sm">
          Manage all franchises in the league. Search filters results on the current page.
        </Text>

        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <TextInput
            placeholder="Search franchises by name, prefix, or GM"
            leftSection={<IconSearch size={16} />}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            className="w-full sm:max-w-xs"
          />
          <Button leftSection={<IconPlus />} className="bg-blue-500 hover:bg-blue-600" onClick={onCreateFranchise} size="sm">
            Create Franchise
          </Button>
        </div>

        <Paper withBorder radius="md" shadow="sm" className="overflow-x-auto">
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            horizontalSpacing="md"
            verticalSpacing="sm"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Franchise</Table.Th>
                <Table.Th>Prefix</Table.Th>
                <Table.Th>General Manager</Table.Th>
                <Table.Th>AGM</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && filtered.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Center py="md">
                      <Loader size="sm" />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : error ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text size="sm" c="red">Failed to load franchises.</Text>
                  </Table.Td>
                </Table.Tr>
              ) : filtered.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                      {query ? "No franchises match your search." : "No franchises found."}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                filtered.map((f) => (
                  <Table.Tr
                    key={f.id}
                    onClick={() => onSelectFranchise(f.id)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open franchise ${f.name}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onSelectFranchise(f.id);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <Table.Td>{f.name}</Table.Td>
                    <Table.Td>{f.prefix || "—"}</Table.Td>
                    <Table.Td>{f.gmName}</Table.Td>
                    <Table.Td>{f.agmName}</Table.Td>
                    <Table.Td>
                      {f.active ? (
                        <Badge color="green" variant="light">Active</Badge>
                      ) : (
                        <Badge color="red" variant="light">Inactive</Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Paper>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <Text size="sm" c={isDark ? "gray.5" : "gray.6"} className="text-center sm:text-left">
            Showing {filtered.length} of {totalCount} franchise{totalCount === 1 ? "" : "s"}
          </Text>
          {pageCount > 1 && (
            <Pagination
              total={pageCount}
              value={activePage}
              onChange={(page) => {
                setActivePage(page);
                refetch({
                  skip: (page - 1) * PAGE_SIZE,
                  take: PAGE_SIZE,
                });
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              size="sm"
              className="justify-center sm:justify-end"
            />
          )}
        </div>
      </div>
    </Container>
  );
}
